import { Country, isEEACountry } from '@getpopsure/public-models';
import {
  getRoutes,
  QuestionnaireQuestions,
  RemoveAnswerType,
  Rule,
  stateManagerHelper,
} from '@getpopsure/qnr-framework';
import {
  removeGenericQuestionnaireAnswer,
  storeGenericQuestionnaireAnswer,
} from 'actions/genericQuestionnaire';
import routes from 'constants/routes';
import { UserWithBrokerMandate } from 'models/user';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { AppState } from 'reducers';
import { getAccountInfo } from 'selectors/user';
import { useSafeTranslation } from 'shared/i18n';
import { isOver } from 'shared/util/isOver';
import { isUnder18 } from 'shared/util/isUnder18';
import { SignupQuestionnaire } from 'SignupQuestionnaire';

import { AGE_UPPER_LIMIT } from './constants';
import {
  Occupation,
  Pension,
  questionGroupsMapping,
  questionnaireIdMapping,
} from './models';
import { getTranslatedQuestionnaire, PensionComponents } from './questionnaire';

const containsEEACountry = (country: Country) => {
  return typeof country === 'object' && isEEACountry(country);
};

const isUSCitizen = (country: Country) => country.code === 'US';

const rules: Rule<Pension>[] = [
  {
    id: 'savingForRetirement',
    if: {
      op: 'equals',
      variable: {
        type: 'static',
        value: false,
      },
    },
    then: {
      goTo: 'privatePensionInfo',
    },
  },
  {
    id: 'privatePensionLongerThan5Years',
    if: {
      op: 'equals',
      variable: {
        type: 'static',
        value: false,
      },
    },
    then: {
      goTo: 'privatePensionLongerThan5YearsInfo',
    },
  },
  {
    id: 'dateOfBirth',
    if: (dateOfBirth) => isUnder18(dateOfBirth as string),
    then: {
      goTo: 'under18Blocker',
    },
  },
  {
    id: 'dateOfBirth',
    if: (dateOfBirth) => isOver(dateOfBirth as string, AGE_UPPER_LIMIT),
    then: {
      goTo: 'over50Blocker',
    },
  },
  {
    id: 'citizenship',
    if: (answer) => containsEEACountry(answer as Country),
    then: {
      goTo: 'identificationDocument',
    },
    else: {
      goTo: 'uploadPassport',
    },
  },
  {
    id: 'identificationDocument',
    if: {
      op: 'equals',
      variable: {
        type: 'static',
        value: 'NATIONAL_ID',
      },
    },
    then: {
      goTo: 'uploadIdCard',
    },
    else: {
      goTo: 'uploadPassport',
    },
  },
  {
    id: 'uploadPassport',
    if: {
      op: 'always',
    },
    then: {
      goTo: 'passportIssueDate',
    },
  },
  {
    id: 'passportIssueDate',
    if: {
      op: 'always',
    },
    then: {
      goTo: 'passportExpiryDate',
    },
  },
  {
    id: 'uploadIdCard',
    if: {
      op: 'always',
    },
    then: {
      goTo: 'idCardIssueDate',
    },
  },
  {
    id: 'idCardIssueDate',
    if: {
      op: 'always',
    },
    then: {
      goTo: 'idCardExpiryDate',
    },
  },
  {
    id: 'occupation',
    if: (occupation, answers) =>
      (occupation as Occupation) === 'EMPLOYED' &&
      isUSCitizen((answers as Pension).citizenship),
    then: {
      goTo: 'paysTaxesInGermany',
    },
  },
  {
    id: 'occupation',
    if: {
      op: 'isIn',
      variable: {
        type: 'static',
        value: [
          'EMPLOYED',
          'SELF_EMPLOYED',
          'APPRENTICE',
          'CIVIL_SERVANT',
          'OTHER',
        ],
      },
    },
    then: {
      goTo: 'jobTitle',
    },
  },
  {
    id: 'paysTaxesInGermany',
    if: {
      op: 'equals',
      variable: {
        type: 'static',
        value: 'NO',
      },
    },
    then: {
      goTo: 'paysTaxesInGermanyBlocker',
    },
  },
];

const removeReview: RemoveAnswerType<Pension> = {
  op: 'always',
  questions: ['review'],
};

const passportAndIdDocumentQuestions: Array<keyof Pension> = [
  'uploadPassport',
  'passportIssueDate',
  'passportExpiryDate',
  'uploadIdCard',
  'idCardIssueDate',
  'idCardExpiryDate',
];

const removePassportAndIdDocuments: RemoveAnswerType<Pension> = {
  op: 'always',
  questions: passportAndIdDocumentQuestions,
};

const removeIdentificationDocument: RemoveAnswerType<Pension> = {
  op: 'always',
  questions: ['identificationDocument', ...passportAndIdDocumentQuestions],
};

const removeAnswersLogic: Partial<
  Record<keyof Pension, RemoveAnswerType<Pension>>
> = {
  quote: removeReview,
  name: removeReview,
  email: removeReview,
  address: removeReview,
  identificationDocument: removePassportAndIdDocuments,
  citizenship: removeIdentificationDocument,
  review: {
    op: 'always',
    questions: [],
  },
};

const prefillNameAndBirthDate = <
  Questionnaire extends QuestionnaireQuestions,
  Key extends keyof Questionnaire
>(
  questionnaireAnswers: Partial<Questionnaire>,
  questionId: Key,
  answer: Questionnaire[Key],
  account?: UserWithBrokerMandate
) => {
  if (
    questionId === 'email' &&
    account &&
    account.email === answer &&
    questionnaireAnswers.email !== answer
  ) {
    let toUpdate: Partial<Pension> = {};
    const { firstName, lastName } = account;

    if (firstName && lastName) {
      toUpdate = { ...toUpdate, name: { firstName, lastName } };
    }
    return toUpdate;
  }

  return null;
};

export const SignupPage = () => {
  const questionnaireAnswers =
    useSelector((state: AppState) => state.genericQuestionnaire.pension) || {};
  const dispatch = useDispatch();
  const account = useSelector(getAccountInfo);

  const onAnswer = <QuestionId extends keyof Pension>(
    questionId: QuestionId,
    answer: Pension[QuestionId]
  ) => {
    dispatch(
      storeGenericQuestionnaireAnswer('pension', { [questionId]: answer })
    );

    const answersToRemove = stateManagerHelper(
      removeAnswersLogic,
      questionnaire.components,
      questionnaireAnswers,
      questionnaire.rules
    ).getAnswersToRemove(questionId, answer);

    dispatch(removeGenericQuestionnaireAnswer('pension', answersToRemove));

    const toUpdate = prefillNameAndBirthDate(
      questionnaireAnswers,
      questionId,
      answer,
      account
    );
    if (toUpdate) {
      dispatch(storeGenericQuestionnaireAnswer('pension', toUpdate));
    }
  };

  const { t } = useSafeTranslation();
  const questions = getTranslatedQuestionnaire(t);

  const questionnaire = {
    components: questions,
    routes: getRoutes(questions, routes.policies.pension.path),
    rules,
  };

  return (
    <Switch>
      <Route path={routes.policies.pension.questionnaire.path}>
        <SignupQuestionnaire
          questionnaireAnswers={questionnaireAnswers}
          questionnaire={questionnaire}
          onAnswer={onAnswer}
          configuration={{
            components: PensionComponents,
            progressBar: {
              questionnaireIdMapping,
              questionGroupsMapping,
            },
          }}
          basePath={routes.policies.pension.path}
          questionId="intro"
          featureName="Pension"
        />
      </Route>
    </Switch>
  );
};
